import OpenAI from 'openai';

export interface ChatGPTOptions {
  model?: string;
  maxTokens?: number;
  temperature?: number;
  timeout?: number;
  maxRetries?: number;
}

export interface AnswerPerspective {
  id: string;
  text: string;
  isExpanded: boolean;
  followUpQuestions: string[];
  isLoadingFollowUps: boolean;
  children: import('../types').ConversationNode[]; // Each perspective has its own conversation tree
  activeFollowUp?: string | null; // Track which follow-up question is currently active
}

/**
 * Create and configure OpenAI client
 * @param apiKey - OpenAI API key
 * @returns Configured OpenAI client
 */
export const createOpenAIClient = (apiKey: string): OpenAI => {
  if (!apiKey || typeof apiKey !== 'string') {
    throw new Error('Valid API key is required');
  }

  return new OpenAI({
    apiKey: apiKey,
    dangerouslyAllowBrowser: true, // Required for client-side usage
  });
};

/**
 * Sleep function for retry delays
 * @param ms - Milliseconds to sleep
 */
const sleep = (ms: number): Promise<void> => new Promise(resolve => setTimeout(resolve, ms));

/**
 * Call ChatGPT API with retry logic and error handling
 * @param client - Configured OpenAI client
 * @param message - User message to send
 * @param options - Configuration options
 * @returns AI response text
 */
export const callChatGPT = async (
  client: OpenAI, 
  message: string, 
  options: ChatGPTOptions = {}
): Promise<string> => {
  const {
    model = 'gpt-3.5-turbo',
    maxTokens = 1000,
    temperature = 0.7,
    timeout = 30000,
    maxRetries = 3
  } = options;

  if (!client) {
    throw new Error('OpenAI client is required');
  }

  if (!message || typeof message !== 'string') {
    throw new Error('Valid message is required');
  }

  const messages = [
    {
      role: 'user' as const,
      content: message.trim()
    }
  ];

  let lastError: any;
  
  for (let attempt = 1; attempt <= maxRetries; attempt++) {
    try {
      // Create timeout promise
      const timeoutPromise = new Promise<never>((_, reject) => {
        setTimeout(() => reject(new Error('Request timeout')), timeout);
      });

      // Create API call promise
      const apiPromise = client.chat.completions.create({
        model,
        messages,
        max_tokens: maxTokens,
        temperature,
      });

      // Race between API call and timeout
      const completion = await Promise.race([apiPromise, timeoutPromise]);

      if (!completion?.choices?.[0]?.message?.content) {
        throw new Error('Invalid response from OpenAI API');
      }

      return completion.choices[0].message.content.trim();

    } catch (error: any) {
      lastError = error;
      
      // Handle specific error types
      if (error.status === 401 || error.message?.includes('Incorrect API key')) {
        throw new Error('Invalid API key. Please check your OpenAI API key.');
      }
      
      if (error.status === 403) {
        throw new Error('API key does not have permission to access this resource.');
      }
      
      if (error.status === 404) {
        throw new Error(`Model "${model}" not found. Please check the model name.`);
      }
      
      if (error.status === 429) {
        // Rate limit - implement exponential backoff
        if (attempt < maxRetries) {
          const delay = Math.min(1000 * Math.pow(2, attempt - 1), 10000);
          console.warn(`Rate limit hit. Retrying in ${delay}ms... (attempt ${attempt}/${maxRetries})`);
          await sleep(delay);
          continue;
        }
        throw new Error('Rate limit exceeded. Please try again later.');
      }
      
      if (error.status >= 500) {
        // Server error - retry with exponential backoff
        if (attempt < maxRetries) {
          const delay = Math.min(1000 * Math.pow(2, attempt - 1), 5000);
          console.warn(`Server error. Retrying in ${delay}ms... (attempt ${attempt}/${maxRetries})`);
          await sleep(delay);
          continue;
        }
        throw new Error('OpenAI service temporarily unavailable. Please try again later.');
      }
      
      if (error.message === 'Request timeout') {
        if (attempt < maxRetries) {
          console.warn(`Request timeout. Retrying... (attempt ${attempt}/${maxRetries})`);
          await sleep(1000);
          continue;
        }
        throw new Error('Request timed out. Please try again.');
      }
      
      // Network or other errors
      if (attempt < maxRetries && (
        error.code === 'ECONNRESET' || 
        error.code === 'ENOTFOUND' || 
        error.message?.includes('network') ||
        error.message?.includes('fetch')
      )) {
        const delay = 1000 * attempt;
        console.warn(`Network error. Retrying in ${delay}ms... (attempt ${attempt}/${maxRetries})`);
        await sleep(delay);
        continue;
      }
      
      // If it's the last attempt or an unrecoverable error, throw
      if (attempt === maxRetries) {
        break;
      }
    }
  }
  
  // If we get here, all retries failed
  throw new Error(
    lastError?.message || 'Failed to get response from OpenAI API after multiple attempts'
  );
};

/**
 * Validate API key format
 * @param apiKey - API key to validate
 * @returns Whether the API key format is valid
 */
export const validateApiKey = (apiKey: string): boolean => {
  if (!apiKey || typeof apiKey !== 'string') {
    return false;
  }
  
  // OpenAI API keys start with 'sk-' and are typically 51 characters long
  return apiKey.startsWith('sk-') && apiKey.length >= 20;
};

/**
 * Get available models (for future use)
 * @param client - Configured OpenAI client
 * @returns List of available models
 */
export const getAvailableModels = async (client: OpenAI): Promise<string[]> => {
  try {
    const models = await client.models.list();
    return models.data
      .filter(model => model.id.includes('gpt'))
      .map(model => model.id)
      .sort();
  } catch (error) {
    console.error('Failed to fetch models:', error);
    return ['gpt-3.5-turbo', 'gpt-4']; // Fallback to known models
  }
};

/**
 * Generate multiple answers/perspectives for a single question
 * @param client - Configured OpenAI client
 * @param question - User's question
 * @param options - Configuration options
 * @returns Array of answer perspectives
 */
export const generateMultipleAnswers = async (
  client: OpenAI,
  question: string,
  options: ChatGPTOptions = {}
): Promise<AnswerPerspective[]> => {
  const {
    model = 'gpt-3.5-turbo',
    maxTokens = 2000,
    temperature = 0.8,
    timeout = 30000,
    maxRetries = 3
  } = options;

  if (!client) {
    throw new Error('OpenAI client is required');
  }

  if (!question || typeof question !== 'string') {
    throw new Error('Valid question is required');
  }

  const prompt = `Please provide 10 different answers or perspectives to this question: '${question.trim()}'

Each answer should:
- Be a complete, standalone response (2-3 sentences)
- Offer a different angle, perspective, or approach
- Be distinct from the other answers
- Be informative and thought-provoking
- Be numbered 1-10

Format as:
1. [First answer/perspective]
2. [Second answer/perspective]
...
10. [Tenth answer/perspective]`;

  const messages = [
    {
      role: 'user' as const,
      content: prompt
    }
  ];

  let lastError: any;
  
  for (let attempt = 1; attempt <= maxRetries; attempt++) {
    try {
      // Create timeout promise
      const timeoutPromise = new Promise<never>((_, reject) => {
        setTimeout(() => reject(new Error('Request timeout')), timeout);
      });

      // Create API call promise
      const apiPromise = client.chat.completions.create({
        model,
        messages,
        max_tokens: maxTokens,
        temperature,
      });

      // Race between API call and timeout
      const completion = await Promise.race([apiPromise, timeoutPromise]);

      if (!completion?.choices?.[0]?.message?.content) {
        throw new Error('Invalid response from OpenAI API');
      }

      const responseText = completion.choices[0].message.content.trim();
      
      // Parse the numbered response
      const answers = parseNumberedAnswers(responseText);
      
      return answers;

    } catch (error: any) {
      lastError = error;
      
      // Handle specific error types (same as callChatGPT)
      if (error.status === 401 || error.message?.includes('Incorrect API key')) {
        throw new Error('Invalid API key. Please check your OpenAI API key.');
      }
      
      if (error.status === 403) {
        throw new Error('API key does not have permission to access this resource.');
      }
      
      if (error.status === 404) {
        throw new Error(`Model "${model}" not found. Please check the model name.`);
      }
      
      if (error.status === 429) {
        // Rate limit - implement exponential backoff
        if (attempt < maxRetries) {
          const delay = Math.min(1000 * Math.pow(2, attempt - 1), 10000);
          console.warn(`Rate limit hit. Retrying in ${delay}ms... (attempt ${attempt}/${maxRetries})`);
          await sleep(delay);
          continue;
        }
        throw new Error('Rate limit exceeded. Please try again later.');
      }
      
      if (error.status >= 500) {
        // Server error - retry with exponential backoff
        if (attempt < maxRetries) {
          const delay = Math.min(1000 * Math.pow(2, attempt - 1), 5000);
          console.warn(`Server error. Retrying in ${delay}ms... (attempt ${attempt}/${maxRetries})`);
          await sleep(delay);
          continue;
        }
        throw new Error('OpenAI service temporarily unavailable. Please try again later.');
      }
      
      if (error.message === 'Request timeout') {
        if (attempt < maxRetries) {
          console.warn(`Request timeout. Retrying... (attempt ${attempt}/${maxRetries})`);
          await sleep(1000);
          continue;
        }
        throw new Error('Request timed out. Please try again.');
      }
      
      // Network or other errors
      if (attempt < maxRetries && (
        error.code === 'ECONNRESET' || 
        error.code === 'ENOTFOUND' || 
        error.message?.includes('network') ||
        error.message?.includes('fetch')
      )) {
        const delay = 1000 * attempt;
        console.warn(`Network error. Retrying in ${delay}ms... (attempt ${attempt}/${maxRetries})`);
        await sleep(delay);
        continue;
      }
      
      // If it's the last attempt or an unrecoverable error, throw
      if (attempt === maxRetries) {
        break;
      }
    }
  }
  
  // If we get here, all retries failed
  throw new Error(
    lastError?.message || 'Failed to get response from OpenAI API after multiple attempts'
  );
};

/**
 * Parse numbered answers from GPT response
 * @param responseText - Raw response from GPT
 * @returns Array of parsed answer perspectives
 */
const parseNumberedAnswers = (responseText: string): AnswerPerspective[] => {
  const lines = responseText.split('\n');
  const answers: AnswerPerspective[] = [];
  
  // Regex to match numbered items (1., 2., etc.)
  const numberRegex = /^\d+\.\s*/;
  
  let currentAnswer = '';
  let answerNumber = 0;
  
  for (const line of lines) {
    const trimmedLine = line.trim();
    
    // Skip empty lines
    if (!trimmedLine) continue;
    
    // Check if this line starts with a number
    if (numberRegex.test(trimmedLine)) {
      // Save previous answer if it exists
      if (currentAnswer && answerNumber > 0) {
        answers.push({
          id: `answer-${answerNumber}`,
          text: currentAnswer.trim(),
          isExpanded: false,
          followUpQuestions: [],
          isLoadingFollowUps: false,
          children: [],
          activeFollowUp: null
        });
      }
      
      // Start new answer
      answerNumber++;
      currentAnswer = trimmedLine.replace(numberRegex, '');
    } else {
      // Continue current answer
      if (currentAnswer) {
        currentAnswer += ' ' + trimmedLine;
      }
    }
  }
  
  // Don't forget the last answer
  if (currentAnswer && answerNumber > 0) {
    answers.push({
      id: `answer-${answerNumber}`,
      text: currentAnswer.trim(),
      isExpanded: false,
      followUpQuestions: [],
      isLoadingFollowUps: false,
      children: [],
      activeFollowUp: null
    });
  }
  
  // Handle case where we don't get exactly 10 answers
  if (answers.length < 10) {
    console.warn(`Expected 10 answers but got ${answers.length}. Adding placeholder answers.`);
    
    // Add placeholder answers to reach 10
    for (let i = answers.length; i < 10; i++) {
      answers.push({
        id: `answer-${i + 1}`,
        text: 'This perspective could not be generated. Please try asking the question again.',
        isExpanded: false,
        followUpQuestions: [],
        isLoadingFollowUps: false,
        children: [],
        activeFollowUp: null
      });
    }
  } else if (answers.length > 10) {
    console.warn(`Expected 10 answers but got ${answers.length}. Trimming to first 10.`);
    // Trim to first 10 answers
    return answers.slice(0, 10);
  }
  
  return answers;
};

/**
 * Generate follow-up questions for a specific answer
 * @param client - Configured OpenAI client
 * @param originalQuestion - The original question that was asked
 * @param selectedAnswer - The specific answer to generate follow-ups for
 * @param options - Configuration options
 * @returns Array of follow-up question strings
 */
export const generateFollowUpQuestions = async (
  client: OpenAI,
  originalQuestion: string,
  selectedAnswer: string,
  options: ChatGPTOptions = {}
): Promise<string[]> => {
  const {
    model = 'gpt-3.5-turbo',
    maxTokens = 1000,
    temperature = 0.7,
    timeout = 30000,
    maxRetries = 3
  } = options;

  if (!client) {
    throw new Error('OpenAI client is required');
  }

  if (!originalQuestion || !selectedAnswer) {
    throw new Error('Valid original question and selected answer are required');
  }

  const prompt = `Based on this question and answer pair, generate 5 thoughtful follow-up questions that would help explore this topic deeper:

Original Question: "${originalQuestion.trim()}"

Selected Answer: "${selectedAnswer.trim()}"

Generate 5 follow-up questions that:
- Dive deeper into specific aspects mentioned in the answer
- Explore related concepts or implications
- Ask for examples, applications, or real-world scenarios
- Challenge assumptions or explore alternative viewpoints
- Help the user understand the topic more comprehensively

Format as:
1. [First follow-up question]
2. [Second follow-up question]
3. [Third follow-up question]
4. [Fourth follow-up question]
5. [Fifth follow-up question]`;

  const messages = [
    {
      role: 'user' as const,
      content: prompt
    }
  ];

  let lastError: any;
  
  for (let attempt = 1; attempt <= maxRetries; attempt++) {
    try {
      // Create timeout promise
      const timeoutPromise = new Promise<never>((_, reject) => {
        setTimeout(() => reject(new Error('Request timeout')), timeout);
      });

      // Create API call promise
      const apiPromise = client.chat.completions.create({
        model,
        messages,
        max_tokens: maxTokens,
        temperature,
      });

      // Race between API call and timeout
      const completion = await Promise.race([apiPromise, timeoutPromise]);

      if (!completion?.choices?.[0]?.message?.content) {
        throw new Error('Invalid response from OpenAI API');
      }

      const responseText = completion.choices[0].message.content.trim();
      
      // Parse the numbered response
      const followUps = parseNumberedFollowUps(responseText);
      
      return followUps;

    } catch (error: any) {
      lastError = error;
      
      // Handle specific error types (same as other functions)
      if (error.status === 401 || error.message?.includes('Incorrect API key')) {
        throw new Error('Invalid API key. Please check your OpenAI API key.');
      }
      
      if (error.status === 403) {
        throw new Error('API key does not have permission to access this resource.');
      }
      
      if (error.status === 404) {
        throw new Error(`Model "${model}" not found. Please check the model name.`);
      }
      
      if (error.status === 429) {
        // Rate limit - implement exponential backoff
        if (attempt < maxRetries) {
          const delay = Math.min(1000 * Math.pow(2, attempt - 1), 10000);
          console.warn(`Rate limit hit. Retrying in ${delay}ms... (attempt ${attempt}/${maxRetries})`);
          await sleep(delay);
          continue;
        }
        throw new Error('Rate limit exceeded. Please try again later.');
      }
      
      if (error.status >= 500) {
        // Server error - retry with exponential backoff
        if (attempt < maxRetries) {
          const delay = Math.min(1000 * Math.pow(2, attempt - 1), 5000);
          console.warn(`Server error. Retrying in ${delay}ms... (attempt ${attempt}/${maxRetries})`);
          await sleep(delay);
          continue;
        }
        throw new Error('OpenAI service temporarily unavailable. Please try again later.');
      }
      
      if (error.message === 'Request timeout') {
        if (attempt < maxRetries) {
          console.warn(`Request timeout. Retrying... (attempt ${attempt}/${maxRetries})`);
          await sleep(1000);
          continue;
        }
        throw new Error('Request timed out. Please try again.');
      }
      
      // Network or other errors
      if (attempt < maxRetries && (
        error.code === 'ECONNRESET' || 
        error.code === 'ENOTFOUND' || 
        error.message?.includes('network') ||
        error.message?.includes('fetch')
      )) {
        const delay = 1000 * attempt;
        console.warn(`Network error. Retrying in ${delay}ms... (attempt ${attempt}/${maxRetries})`);
        await sleep(delay);
        continue;
      }
      
      // If it's the last attempt or an unrecoverable error, throw
      if (attempt === maxRetries) {
        break;
      }
    }
  }
  
  // If we get here, all retries failed
  throw new Error(
    lastError?.message || 'Failed to get follow-up questions from OpenAI API after multiple attempts'
  );
};

/**
 * Parse numbered follow-up questions from GPT response
 * @param responseText - Raw response from GPT
 * @returns Array of follow-up question strings
 */
const parseNumberedFollowUps = (responseText: string): string[] => {
  const lines = responseText.split('\n');
  const followUps: string[] = [];
  
  // Regex to match numbered items (1., 2., etc.)
  const numberRegex = /^\d+\.\s*/;
  
  let currentQuestion = '';
  
  for (const line of lines) {
    const trimmedLine = line.trim();
    
    // Skip empty lines
    if (!trimmedLine) continue;
    
    // Check if this line starts with a number
    if (numberRegex.test(trimmedLine)) {
      // Save previous question if it exists
      if (currentQuestion) {
        followUps.push(currentQuestion.trim());
      }
      
      // Start new question
      currentQuestion = trimmedLine.replace(numberRegex, '');
    } else {
      // Continue current question
      if (currentQuestion) {
        currentQuestion += ' ' + trimmedLine;
      }
    }
  }
  
  // Don't forget the last question
  if (currentQuestion) {
    followUps.push(currentQuestion.trim());
  }
  
  // Ensure we have exactly 5 follow-up questions
  if (followUps.length < 5) {
    console.warn(`Expected 5 follow-up questions but got ${followUps.length}. Adding placeholder questions.`);
    
    // Add placeholder questions to reach 5
    for (let i = followUps.length; i < 5; i++) {
      followUps.push(`Follow-up question ${i + 1} could not be generated. Please try again.`);
    }
  } else if (followUps.length > 5) {
    console.warn(`Expected 5 follow-up questions but got ${followUps.length}. Trimming to first 5.`);
    // Trim to first 5 questions
    return followUps.slice(0, 5);
  }
  
  return followUps;
};

/**
 * Generate a single comprehensive answer for a follow-up question
 * @param client - Configured OpenAI client
 * @param question - The follow-up question to answer
 * @param context - Optional context from the parent conversation
 * @param options - Configuration options
 * @returns Single comprehensive answer and follow-up questions
 */
export const generateComprehensiveAnswer = async (
  client: OpenAI,
  question: string,
  context?: string,
  options: ChatGPTOptions = {}
): Promise<{ answer: string; followUpQuestions: string[] }> => {
  if (!client) {
    throw new Error('OpenAI client is required');
  }

  if (!question || typeof question !== 'string') {
    throw new Error('Valid question is required');
  }

  const {
    model = 'gpt-3.5-turbo',
    maxTokens = 2000,
    temperature = 0.7
  } = options;

  // Create a comprehensive prompt for follow-up questions
  let prompt = `Please provide a comprehensive, detailed answer to the following question: "${question}"`;
  
  if (context) {
    prompt += `\n\nContext from previous conversation: ${context}`;
  }
  
  prompt += `\n\nPlease structure your response as follows:
1. First, provide a thorough, detailed answer to the question (aim for 200-400 words)
2. Then, on a new line, write "FOLLOW_UP_QUESTIONS:"
3. After that, provide exactly 5 follow-up questions that would help explore this topic deeper, numbered 1-5

Make sure your answer is comprehensive and informative, and that the follow-up questions are thoughtful and would lead to meaningful deeper exploration of the topic.`;

  try {
    const response = await callChatGPT(client, prompt, { model, maxTokens, temperature });
    
    // Split the response into answer and follow-up questions
    const parts = response.split('FOLLOW_UP_QUESTIONS:');
    
    if (parts.length !== 2) {
      // Fallback if the format isn't followed exactly
      return {
        answer: response,
        followUpQuestions: [
          "Can you elaborate on this topic further?",
          "What are the practical implications of this?",
          "How does this relate to other concepts?",
          "What are the potential challenges or limitations?",
          "What would be the next steps to explore this deeper?"
        ]
      };
    }
    
    const answer = parts[0].trim();
    const followUpSection = parts[1].trim();
    
    // Parse the follow-up questions
    const followUpQuestions = parseNumberedFollowUps(followUpSection);
    
    return {
      answer,
      followUpQuestions
    };
    
  } catch (error) {
    console.error('Error generating comprehensive answer:', error);
    throw error;
  }
}; 